import type { Breadcrumb, ErrorEvent } from '@sentry/vue'
import * as Sentry from '@sentry/vue'
import { useRouter, useRuntimeConfig } from '#app'
import { useSentry } from '~/composables/monitoring/useSentry'
import { useMonitoring } from '~/composables/monitoring/useMonitoring'

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig().public
  const router = useRouter()
  const authStore = useAuthStore()
  const { setUserContext, updateLicenceContext } = useSentry()
  const { logEvent } = useMonitoring()

  const rates = {
    local: {
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0
    },
    development: {
      tracesSampleRate: 1,
      replaysSessionSampleRate: 1
    },
    staging: {
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.02
    },
    production: {
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.01
    }
  }
  type rateEnv = keyof typeof rates

  const sentryEnv: rateEnv = (config.sentry.env as unknown as rateEnv) || 'local'
  const envRate = rates[sentryEnv] || rates.local

  // Environment-specific settings
  const isLocal = sentryEnv === 'local'
  const isProduction = sentryEnv === 'production'

  Sentry.init({
    dsn: config.sentry.dsn,
    environment: sentryEnv,
    release: config.sentry.release,
    dist: config.sentry.dist || undefined,

    // Core settings
    debug: false, // Set to true for detailed logs in the console
    attachStacktrace: true,
    enabled: Boolean(config.sentry.dsn),
    normalizeDepth: 10, // Deeper object serialization for better context
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: []
    },

    integrations: [
      Sentry.browserTracingIntegration({
        router,
        enableLongTask: true,
        enableInp: true
      }),

      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
        mask: ['.sensitive-data', 'input[type="password"]', '.mask-text'],
        block: ['.pii-data'],

        // Network capture settings
        networkDetailAllowUrls: [config.apiUrl, 'localhost', window.location.origin],
        networkRequestHeaders: ['X-Request-ID', 'X-Fundamentum-Licence-Id'],
        networkResponseHeaders: ['X-Response-ID', 'X-RateLimit-Remaining'],

        // Only capture network errors in production to reduce noise
        networkCaptureBodies: isProduction
      }),

      Sentry.vueIntegration({
        Vue: nuxtApp.vueApp,
        attachProps: true,
        attachErrorHandler: true,
        tracingOptions: {
          trackComponents: !isProduction,
          timeout: 2000,
          hooks: ['mount', 'unmount', 'update']
        }
      }),

      Sentry.breadcrumbsIntegration({
        dom: true,
        fetch: true,
        history: true,
        sentry: true,
        xhr: true
      }),

      Sentry.httpContextIntegration(),

      Sentry.captureConsoleIntegration({
        levels: isLocal ? [] : ['error'] // Only capture errors to prevent infinite loop with Vue warnings during HMR
      }),

      Sentry.dedupeIntegration(),

      Sentry.extraErrorDataIntegration({
        depth: 5
      }),

      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true
      })
    ],

    // Sampling rates by environment
    tracesSampleRate: envRate.tracesSampleRate || 0,
    replaysSessionSampleRate: envRate.replaysSessionSampleRate || 0,
    replaysOnErrorSampleRate: 0.1,

    // Trace propagation for frontend-backend correlation
    // Un-comment and adjust the following if we follow through with backend tracing
    // tracePropagationTargets: [
    //   'localhost',
    //   config.apiUrl,
    //   /^https:\/\/.*\.fundamentum\.(test|com)/ // Adjust to your domains
    // ],

    maxBreadcrumbs: 50,

    beforeSend(event: ErrorEvent) {
      if (event.contexts) {
        event.contexts.browser = {
          ...event.contexts.browser,
          user_agent: navigator.userAgent
        }
      }

      if (isProduction) {
        // Skip potential third-party script errors
        if (
          event.exception?.values?.[0]?.stacktrace?.frames?.some(
            (frame) =>
              frame.filename?.includes('cdn.') ||
              frame.filename?.includes('gtag') ||
              frame.filename?.includes('analytics')
          )
        ) {
          return null
        }
      }

      return event
    },

    beforeBreadcrumb(breadcrumb: Breadcrumb) {
      // Filter out Vue compiler warnings to prevent infinite loop
      if (breadcrumb.category === 'console' && breadcrumb.message) {
        if (breadcrumb.message.includes('compilerOptions') || breadcrumb.message.includes('[Vue warn]')) {
          return null
        }

        breadcrumb.data = {
          ...breadcrumb.data,
          timestamp: new Date().toISOString()
        }
      }

      if (isProduction) {
        // Skip Vue internal breadcrumbs
        if (breadcrumb.message?.includes('intlify')) {
          return null
        }
      }

      return breadcrumb
    },

    ignoreErrors: [
      // Browser quirks
      'ResizeObserver loop',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',

      // Vue warnings - CRITICAL: Prevent infinite loop during HMR
      /\[Vue warn\].*compilerOptions/,
      /The compilerOptions config option/,
      /\[Vue warn\]/,

      // Network issues
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'The operation was aborted',

      // Vue Router
      'NavigationDuplicated',
      'NavigationCancelled',

      // Browser extensions
      /extension\//,
      /^chrome:\/\//,
      /^moz-extension:\/\//
    ],

    ignoreSpans: [
      'intlify message evaluation',
      'intlify message compilation',
      'intlify message resolve',
      /^\[Vue warn\]/
      // Add any other noisy transactions
    ],

    initialScope: {
      tags: {
        component: 'spa',
        framework: 'nuxt3',
        runtime: 'browser'
      },
      contexts: {
        app: {
          app_name: 'SCMS Viewer',
          app_version: config.sentry.release,
          environment: sentryEnv
        }
      }
    }
  })

  // Add navigation tracking
  router.afterEach((to, from) => {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated from ${from.path} to ${to.path}`,
      level: 'info',
      data: {
        from: {
          name: from.name,
          path: from.path,
          params: from.params
        },
        to: {
          name: to.name,
          path: to.path,
          params: to.params
        }
      }
    })
  })

  watch(
    () => authStore.activeLicence,
    async (newLicence) => {
      if (!newLicence || !authStore.user) return

      setUserContext(authStore.user)
      logEvent('Licence changed', 'info', { licenceId: newLicence.id.toString() })
      updateLicenceContext(newLicence)
    }
  )
})
